import React, { useEffect, useState } from "react";
import { Avatar, Transfer, Typography, notification } from "antd";
import type { TransferItem } from "antd/es/transfer";
import { useParams } from "react-router-dom";
import { getPaymentMethods } from "../../PaymentMethods/action";
import {
  getBettingWebsiteByID,
  pullFromBettingWebsitePaymentGateways,
  pushIntoBettingWebsitePaymentGateways,
} from "../action";
import { IBettingWebsitePaymentGatway } from "../../../types/BettingWebsite.PaymentGateway";
import { PAYMENT_METHOND_CLOUD_URL } from "../../../lib/env";

const { Title } = Typography;

const EditPaymentGateways: React.FC<{ fetchData: () => void }> = ({
  fetchData,
}) => {
  const { id } = useParams();
  const [paymentsMethods, setPaymentMethods] = useState<
    IBettingWebsitePaymentGatway[]
  >([]);
  const [target, setTarget] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const fetchPaymentMethods = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const [allMethds, website] = await Promise.all([
        getPaymentMethods(),
        getBettingWebsiteByID(id),
      ]);

      setPaymentMethods(allMethds);
      const selectedIds =
        website?.PaymentGateways?.map((mthd: IBettingWebsitePaymentGatway) =>
          String(mthd.id)
        ) || [];
      setTarget(selectedIds);
    } catch (err: any) {
      api.error({
        message: "Failed to load data",
        description: err?.message || "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, [id]);

  const handleChange = async (
    nextTargetKeys: (string | number)[],
    _direction: "left" | "right",
    _moveKeys: (string | number)[]
  ) => {
    const stringNextTargetKeys = nextTargetKeys.map(String);

    const toPush = stringNextTargetKeys.filter((id) => !target.includes(id));
    const toPull = target.filter((id) => !stringNextTargetKeys.includes(id));

    setTarget(stringNextTargetKeys);
    setIsLoading(true);

    try {
      for (const langId of toPush) {
        await pushIntoBettingWebsitePaymentGateways(id!, langId);
      }

      for (const langId of toPull) {
        await pullFromBettingWebsitePaymentGateways(id!, langId);
      }

      api.success({ message: "Payment gateways updated successfully" });
      fetchData();
    } catch (err: any) {
      api.error({
        message: "Failed to update payment gateways",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const transferData: TransferItem[] = paymentsMethods.map((mthd) => ({
    key: String(mthd.id),
    title: mthd.PaymentGatwayName.En ?? "",
    flag: mthd.Logo,
  }));

  return (
    <>
      {contextHolder}
      <Transfer
        dataSource={transferData}
        targetKeys={target}
        onChange={handleChange}
        titles={["Available Gateways", "Selected Gateways"]}
        render={(item) => {
          const flag = (item as any).flag || "";
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar src={PAYMENT_METHOND_CLOUD_URL + flag} size="small" />
              <span>{item.title}</span>
            </div>
          );
        }}
        filterOption={(inputValue, item) =>
          (item.title ?? "").toLowerCase().includes(inputValue.toLowerCase())
        }
        oneWay
        disabled={isLoading}
        pagination
        showSearch
        style={{ width: "100%" }} // make the Transfer full width
        listStyle={{
          width: "48%", // each list takes 48% width
          height: 400,
        }}
      />
    </>
  );
};

export default EditPaymentGateways;
